/**
 * Route constants and mappings for the admin dashboard
 *
 * This file contains:
 * - ROUTE_PATHS: All available routes in the application
 * - ROUTE_TO_SLIDER_MAP: Maps routes to slider values for backward compatibility
 * - SLIDER_TO_ROUTE_MAP: Reverse mapping for navigation
 * - MENU_STRUCTURE: Organized menu structure with submenus for better UX
 * - NAVIGATION_ITEMS: Flat list of all navigable items (backward compatibility)
 * - Helper functions for route and menu management
 */
export const ROUTE_PATHS = {
  // Authentication routes
  ROOT: "/",
  LOGIN: "/login",

  // Dashboard base route
  DASHBOARD: "/dashboard",

  // Dashboard page routes
  HOME: "/dashboard/home",
  WHITELABEL_HOME: "/dashboard/whitelabel-home",
  ALL_USER: "/dashboard/all-user",
  NO_ACCOUNT_USER: "/dashboard/no-account-user",
  OKR_DASHBOARD: "/dashboard/okr-dashboard",
  TAGMANGO_CREATORS: "/dashboard/tagmango-creators",
  FREEDOM_CREATORS: "/dashboard/freedom-creators",
  ENTERPRISE_CREATORS: "/dashboard/enterprise-creators",
  USER_KYC_LIST: "/dashboard/user-kyc-list",
  MANAGE_FEATURES: "/dashboard/manage-features",
  DASHBOARD_BANNER: "/dashboard/dashboard-banner",
  ONBOARDING_DETAILS: "/dashboard/onboarding-details",
  WITHDRAWALS_SECTION: "/dashboard/withdrawals-section",
  CREATOR_PAYABLES: "/dashboard/creator-payables",
  INDIAN_WALLET_WITHDRAWALS: "/dashboard/indian-wallet-withdrawals",
  INTERNATIONAL_WALLET_WITHDRAWALS:
    "/dashboard/international-wallet-withdrawals",
  USD_WALLET_WITHDRAWALS: "/dashboard/usd-wallet-withdrawals",
  TAX_REPORTS: "/dashboard/tax-reports",
  MANGO_CREDIT_TRANSACTIONS: "/dashboard/mango-credit-transactions",
  INVOICES: "/dashboard/invoices",
  UPCOMING_MANGOS: "/dashboard/upcoming-mangos",
  ORDER_DETAILS: "/dashboard/order-details",
  REFUNDS: "/dashboard/refunds",
  CREATOR_EARNINGS: "/dashboard/creator-earnings",
  AFFILIATE_EARNINGS: "/dashboard/affiliate-earnings",
  USER_DETAILS_LIST: "/dashboard/user-details-list",
  PUSH_NOTIFICATIONS: "/dashboard/push-notifications",
  COUPON_SECTION: "/dashboard/coupon-section",
  EMAIL_SECTION: "/dashboard/email-section",
  DASHBOARD_ACCESS: "/dashboard/dashboard-access",
  BYTESCARE_REQUESTS: "/dashboard/bytescare-requests",
  MIGRATION: "/dashboard/migration",
  FRONTEND_RELEASES: "/dashboard/frontend-releases",
  WHATSAPP_MANAGEMENT: "/dashboard/whatsapp-management",
  REFERN_EARN_REQUESTS: "/dashboard/refern-earn-requests",
  REFERN_EARN_PRODUCTS: "/dashboard/refern-earn-products",
  REFERN_EARN_PARTNERS: "/dashboard/refern-earn-partners",
  REFERN_EARN_NON_EARNING_PARTNERS:
    "/dashboard/refern-earn-non-earning-partners",
  REFERN_EARN_PAYOUTS: "/dashboard/refern-earn-payouts",
  MANAGE_PLAN_REQUESTS: "/dashboard/manage-plan-requests",
  TIER_ORDERS_V2: "/dashboard/tier-orders-v2",
  LANDING_PAGE_TEMPLATES: "/dashboard/landing-page-templates",

  // Dynamic routes with parameters
  USER_DETAILS: "/dashboard/user-details/:userId",
  NON_TRANSFERRED: "/dashboard/non-transferred",
  PAYMENT_TERMS: "/dashboard/payment-terms",
  PAYOUTS_SCHEDULE: "/dashboard/payouts-schedule",
  CREATOR_ACCESS: "/dashboard/creator-access",
  CERTIFICATES_SECTION: "/dashboard/certificates-section",
  CREATORS_FUNNEL: "/dashboard/creators-funnel",
  REFERN_EARN_EARNINGS: "/dashboard/refern-earn-earnings",
  TIER_ORDERS: "/dashboard/tier-orders",

  // Sales
  SALES_DASHBOARD: "/dashboard/sales-dashboard",
  ITEMS_SALES_DASHBOARD: "/dashboard/items-sales-dashboard",
  MANGO_CONFIG: "/dashboard/mango-config",
  CREATOR_LIST: "/dashboard/creator-list",
  TRANSACTIONS: "/dashboard/transactions",
  UTM_PARAMETERS: "/dashboard/utm-parameters",
  MANUAL_PAYMENTS: "/dashboard/manual-payments",
};

// Mapping between route paths and slider values (for backward compatibility)
export const ROUTE_TO_SLIDER_MAP = {
  [ROUTE_PATHS.HOME]: "home",
  [ROUTE_PATHS.WHITELABEL_HOME]: "whitelabelHome",
  [ROUTE_PATHS.ALL_USER]: "allUser",
  [ROUTE_PATHS.NO_ACCOUNT_USER]: "noAccountUser",
  [ROUTE_PATHS.TAGMANGO_CREATORS]: "tagmangoCreators",
  [ROUTE_PATHS.FREEDOM_CREATORS]: "freedomCreators",
  [ROUTE_PATHS.ENTERPRISE_CREATORS]: "enterpriseCreators",
  [ROUTE_PATHS.USER_KYC_LIST]: "userKYCList",
  [ROUTE_PATHS.MANAGE_FEATURES]: "manageFeatures",
  [ROUTE_PATHS.DASHBOARD_BANNER]: "dashboardBanner",
  [ROUTE_PATHS.ONBOARDING_DETAILS]: "onboardingDetails",
  [ROUTE_PATHS.WITHDRAWALS_SECTION]: "widthdrawalsSection",
  [ROUTE_PATHS.CREATOR_PAYABLES]: "creatorPayables",
  [ROUTE_PATHS.INDIAN_WALLET_WITHDRAWALS]: "indianWalletWithdrawals",
  [ROUTE_PATHS.INTERNATIONAL_WALLET_WITHDRAWALS]:
    "internationalWalletWithdrawals",
  [ROUTE_PATHS.USD_WALLET_WITHDRAWALS]: "usdWalletWithdrawals",
  [ROUTE_PATHS.TAX_REPORTS]: "taxReports",
  [ROUTE_PATHS.MANGO_CREDIT_TRANSACTIONS]: "mangoCreditTransactions",
  [ROUTE_PATHS.INVOICES]: "invoices",
  [ROUTE_PATHS.UPCOMING_MANGOS]: "upcomingMangos",
  [ROUTE_PATHS.OKR_DASHBOARD]: "csDashboard",
  [ROUTE_PATHS.ORDER_DETAILS]: "orderDetails",
  [ROUTE_PATHS.REFUNDS]: "refunds",
  [ROUTE_PATHS.CREATOR_EARNINGS]: "creatorEarnings",
  [ROUTE_PATHS.AFFILIATE_EARNINGS]: "affiliateEarnings",
  [ROUTE_PATHS.USER_DETAILS_LIST]: "userDetailsList",
  [ROUTE_PATHS.PUSH_NOTIFICATIONS]: "pushNotifications",
  [ROUTE_PATHS.COUPON_SECTION]: "couponSection",
  [ROUTE_PATHS.EMAIL_SECTION]: "emailSection",
  [ROUTE_PATHS.DASHBOARD_ACCESS]: "dashboardAccess",
  [ROUTE_PATHS.BYTESCARE_REQUESTS]: "bytescareRequests",
  [ROUTE_PATHS.MIGRATION]: "migration",
  [ROUTE_PATHS.FRONTEND_RELEASES]: "frontendReleases",
  [ROUTE_PATHS.WHATSAPP_MANAGEMENT]: "whatsappManagement",
  [ROUTE_PATHS.REFERN_EARN_REQUESTS]: "refernEarnRequests",
  [ROUTE_PATHS.REFERN_EARN_PRODUCTS]: "refernEarnProducts",
  [ROUTE_PATHS.REFERN_EARN_PARTNERS]: "refernEarnPartners",
  [ROUTE_PATHS.REFERN_EARN_NON_EARNING_PARTNERS]:
    "refernEarnNonEarningPartners",
  [ROUTE_PATHS.REFERN_EARN_PAYOUTS]: "refernEarnPayouts",
  [ROUTE_PATHS.MANAGE_PLAN_REQUESTS]: "managePlanRequests",
  [ROUTE_PATHS.TIER_ORDERS_V2]: "tierOrdersV2",
  [ROUTE_PATHS.LANDING_PAGE_TEMPLATES]: "landingPageTemplates",
  [ROUTE_PATHS.USER_DETAILS]: "userDetails",
  [ROUTE_PATHS.NON_TRANSFERRED]: "nonTransferred",
  [ROUTE_PATHS.PAYMENT_TERMS]: "paymentTerms",
  [ROUTE_PATHS.PAYOUTS_SCHEDULE]: "payoutsSchedule",
  [ROUTE_PATHS.CREATOR_ACCESS]: "creatorAccess",
  [ROUTE_PATHS.CERTIFICATES_SECTION]: "certificatesSection",
  [ROUTE_PATHS.CREATORS_FUNNEL]: "creatorsFunnel",
  [ROUTE_PATHS.REFERN_EARN_EARNINGS]: "refernEarnEarnings",
  [ROUTE_PATHS.TIER_ORDERS]: "tierOrders",
  [ROUTE_PATHS.SALES_DASHBOARD]: "salesDashboard",
  [ROUTE_PATHS.MANGO_CONFIG]: "mangoConfig",
  [ROUTE_PATHS.CREATOR_LIST]: "creatorList",
  [ROUTE_PATHS.TRANSACTIONS]: "transactions",
  [ROUTE_PATHS.UTM_PARAMETERS]: "utmParameters",
  [ROUTE_PATHS.MANUAL_PAYMENTS]: "manualPayments",
};

// Reverse mapping from slider values to route paths
export const SLIDER_TO_ROUTE_MAP = Object.entries(ROUTE_TO_SLIDER_MAP).reduce(
  (acc, [route, slider]) => ({
    ...acc,
    [slider]: route,
  }),
  {}
);

// Organized navigation menu with submenus for better UX
export const MENU_STRUCTURE = [
  // Dashboard section
  {
    type: "group",
    key: "dashboard-group",
    label: "Dashboard",
    items: [
      { key: "home", name: "Home", route: ROUTE_PATHS.HOME },
      {
        key: "whitelabelHome",
        name: "Whitelabel Home",
        route: ROUTE_PATHS.WHITELABEL_HOME,
      },
    ],
  },

  // User Management
  {
    type: "submenu",
    key: "user-management",
    label: "User Management",
    icon: "UserOutlined",
    items: [
      { key: "allUser", name: "All Users", route: ROUTE_PATHS.ALL_USER },
      {
        key: "noAccountUser",
        name: "Users with No Bank Account",
        route: ROUTE_PATHS.NO_ACCOUNT_USER,
      },
      {
        key: "userDetailsList",
        name: "User Details List",
        route: ROUTE_PATHS.USER_DETAILS_LIST,
      },
      {
        key: "userKYCList",
        name: "Manage KYC",
        route: ROUTE_PATHS.USER_KYC_LIST,
      },
    ],
  },

  // User Insights
  {
    type: "submenu",
    key: "insights",
    label: "Insights",
    icon: "BarChartOutlined",
    items: [
      {
        key: "csDashboard",
        name: "CS Dashboard",
        route: ROUTE_PATHS.OKR_DASHBOARD,
      },
    ],
  },

  // Creator Management
  {
    type: "submenu",
    key: "creator-management",
    label: "Creator Management",
    icon: "TeamOutlined",
    items: [
      {
        key: "tagmangoCreators",
        name: "Tagmango Creators",
        route: ROUTE_PATHS.TAGMANGO_CREATORS,
      },
      {
        key: "freedomCreators",
        name: "Freedom Creators",
        route: ROUTE_PATHS.FREEDOM_CREATORS,
      },
      {
        key: "enterpriseCreators",
        name: "Enterprise Creators",
        route: ROUTE_PATHS.ENTERPRISE_CREATORS,
      },
      {
        key: "creatorEarnings",
        name: "Creator Earnings",
        route: ROUTE_PATHS.CREATOR_EARNINGS,
      },
      {
        key: "creatorPayables",
        name: "Creator Payables",
        route: ROUTE_PATHS.CREATOR_PAYABLES,
      },
    ],
  },

  // Orders & Payments
  {
    type: "submenu",
    key: "orders-payments",
    label: "Orders & Payments",
    icon: "ShoppingCartOutlined",
    items: [
      // finally we know our user of this, Ravi Bhai
      {
        key: "orderDetails",
        name: "Order Details",
        route: ROUTE_PATHS.ORDER_DETAILS,
      },
      { key: "refunds", name: "Refunds", route: ROUTE_PATHS.REFUNDS },
      {
        key: "tierOrdersV2",
        name: "Tier Orders v2",
        route: ROUTE_PATHS.TIER_ORDERS_V2,
      },
    ],
  },

  // Sales Management
  {
    type: "submenu",
    key: "sales-management",
    label: "Sales Dashboard",
    icon: "BarChartOutlined",
    items: [
      {
        key: "salesDashboard",
        name: "Sales",
        route: ROUTE_PATHS.SALES_DASHBOARD,
      },

      {
        key: "mangoConfig",
        name: "Mango Config",
        route: ROUTE_PATHS.MANGO_CONFIG,
      },
      {
        key: "creatorList",
        name: "Creator List",
        route: ROUTE_PATHS.CREATOR_LIST,
      },
      {
        key: "transactions",
        name: "Transactions",
        route: ROUTE_PATHS.TRANSACTIONS,
      },
      {
        key: "utmParameters",
        name: "UTM Parameters",
        route: ROUTE_PATHS.UTM_PARAMETERS,
      },
      {
        key: "manualPayments",
        name: "Manual Payments",
        route: ROUTE_PATHS.MANUAL_PAYMENTS,
      },
    ],
  },

  // Financial Management
  {
    type: "submenu",
    key: "financial-management",
    label: "Financial Management",
    icon: "DollarCircleOutlined",
    items: [
      {
        key: "widthdrawalsSection",
        name: "Withdrawal Section",
        route: ROUTE_PATHS.WITHDRAWALS_SECTION,
      },
      {
        key: "indianWalletWithdrawals",
        name: "Wallet Withdrawals (IND)",
        route: ROUTE_PATHS.INDIAN_WALLET_WITHDRAWALS,
      },
      {
        key: "internationalWalletWithdrawals",
        name: "Wallet Withdrawals (INTL)",
        route: ROUTE_PATHS.INTERNATIONAL_WALLET_WITHDRAWALS,
      },
      {
        key: "usdWalletWithdrawals",
        name: "Wallet Withdrawals (USD - FIRC)",
        route: ROUTE_PATHS.USD_WALLET_WITHDRAWALS,
      },
      {
        key: "taxReports",
        name: "Tax Reports",
        route: ROUTE_PATHS.TAX_REPORTS,
      },
      {
        key: "mangoCreditTransactions",
        name: "Mango Credit Transactions",
        route: ROUTE_PATHS.MANGO_CREDIT_TRANSACTIONS,
      },
      { key: "invoices", name: "Invoices", route: ROUTE_PATHS.INVOICES },
      {
        key: "affiliateEarnings",
        name: "Affiliate Earnings",
        route: ROUTE_PATHS.AFFILIATE_EARNINGS,
      },
    ],
  },

  // Refer & Earn
  {
    type: "submenu",
    key: "refer-earn",
    label: "Refer & Earn",
    icon: "ShareAltOutlined",
    items: [
      {
        key: "refernEarnRequests",
        name: "Requests",
        route: ROUTE_PATHS.REFERN_EARN_REQUESTS,
      },
      {
        key: "refernEarnProducts",
        name: "Products",
        route: ROUTE_PATHS.REFERN_EARN_PRODUCTS,
      },
      {
        key: "refernEarnPartners",
        name: "Partners",
        route: ROUTE_PATHS.REFERN_EARN_PARTNERS,
      },
      {
        key: "refernEarnNonEarningPartners",
        name: "Non Earning Partners",
        route: ROUTE_PATHS.REFERN_EARN_NON_EARNING_PARTNERS,
      },
      {
        key: "refernEarnPayouts",
        name: "Payouts",
        route: ROUTE_PATHS.REFERN_EARN_PAYOUTS,
      },
    ],
  },

  // Content & Marketing
  {
    type: "submenu",
    key: "content-marketing",
    label: "Content & Marketing",
    icon: "BulbOutlined",
    items: [
      {
        key: "upcomingMangos",
        name: "Upcoming Mangos",
        route: ROUTE_PATHS.UPCOMING_MANGOS,
      },
      {
        key: "landingPageTemplates",
        name: "Website Templates",
        route: ROUTE_PATHS.LANDING_PAGE_TEMPLATES,
      },
      {
        key: "couponSection",
        name: "Coupon Management",
        route: ROUTE_PATHS.COUPON_SECTION,
      },
    ],
  },

  // Communication
  {
    type: "submenu",
    key: "communication",
    label: "Communication",
    icon: "MessageOutlined",
    items: [
      {
        key: "pushNotifications",
        name: "Push Notifications",
        route: ROUTE_PATHS.PUSH_NOTIFICATIONS,
      },
      {
        key: "emailSection",
        name: "Email Management",
        route: ROUTE_PATHS.EMAIL_SECTION,
      },
      {
        key: "whatsappManagement",
        name: "WhatsApp Management",
        route: ROUTE_PATHS.WHATSAPP_MANAGEMENT,
      },
    ],
  },

  // Administration
  {
    type: "submenu",
    key: "administration",
    label: "Administration",
    icon: "SettingOutlined",
    items: [
      {
        key: "dashboardAccess",
        name: "Dashboard Access",
        route: ROUTE_PATHS.DASHBOARD_ACCESS,
      },
      {
        key: "manageFeatures",
        name: "Manage Features",
        route: ROUTE_PATHS.MANAGE_FEATURES,
      },
      {
        key: "dashboardBanner",
        name: "Dashboard Banner",
        route: ROUTE_PATHS.DASHBOARD_BANNER,
      },
      {
        key: "onboardingDetails",
        name: "Onboarding Details",
        route: ROUTE_PATHS.ONBOARDING_DETAILS,
      },
      {
        key: "managePlanRequests",
        name: "Manage Plan Requests",
        route: ROUTE_PATHS.MANAGE_PLAN_REQUESTS,
      },
    ],
  },

  // System & Tools
  {
    type: "submenu",
    key: "system-tools",
    label: "System & Tools",
    icon: "ToolOutlined",
    items: [
      {
        key: "bytescareRequests",
        name: "Bytescare Requests",
        route: ROUTE_PATHS.BYTESCARE_REQUESTS,
      },
      { key: "migration", name: "Migration", route: ROUTE_PATHS.MIGRATION },
      {
        key: "frontendReleases",
        name: "Whitelabel V2 Release",
        route: ROUTE_PATHS.FRONTEND_RELEASES,
      },
    ],
  },
];

// Flat list for backward compatibility
export const NAVIGATION_ITEMS = MENU_STRUCTURE.reduce((acc, section) => {
  if (section.type === "group") {
    return [...acc, ...section.items];
  }
  if (section.type === "submenu") {
    return [...acc, ...section.items];
  }
  return acc;
}, []);

// Helper function to get the parent menu key for a given item key
export const getParentMenuKey = (itemKey) => {
  const parentSection = MENU_STRUCTURE.filter(
    (section) => section.type === "submenu"
  ).find((section) => section.items.some((item) => item.key === itemKey));

  return parentSection ? parentSection.key : null;
};

// Default route for authenticated users
export const DEFAULT_DASHBOARD_ROUTE = ROUTE_PATHS.HOME;

// Helper function to get route path by slider key
export const getRouteBySliderKey = (sliderKey) => {
  return SLIDER_TO_ROUTE_MAP[sliderKey] || ROUTE_PATHS.HOME;
};

// Helper function to get slider key by route path
export const getSliderKeyByRoute = (routePath) => {
  return ROUTE_TO_SLIDER_MAP[routePath] || "home";
};

// Helper function to check if a route requires authentication
export const isProtectedRoute = (pathname) => {
  return pathname.startsWith("/dashboard");
};

// Helper function to generate user details route
export const getUserDetailsRoute = (userId) => {
  return ROUTE_PATHS.USER_DETAILS.replace(":userId", userId);
};
